import { useEffect, useMemo, useState } from 'react';
import { Plug, Sparkles, Facebook, MapPin, MessageSquare, Mail, Pencil, Lock, AlertTriangle, ShoppingBag, KeyRound, RefreshCw } from 'lucide-react';
import ModulLayout from '../layout/ModulLayout';
import ListToolbar from '../layout/ListToolbar';
import KDoreseniRadek, { KDoreseniSeznam } from '../layout/KDoreseni';
import ZpetTlacitko from '../ui/ZpetTlacitko';
import ModalShell from '../ui/ModalShell';
import SmsPrehled from '../SmsPrehled';
import SmsSablony from '../SmsSablony';
import { PohledPill } from '../layout/ListToolbar';
import { muze } from '../../lib/moduly';
import {
    SECTION_COLOR, CERVENA, ZELENA, nacti, posli, tlumene, cara,
    Nadpis, Pole, RadekPanelu, Tlacitko, PatickaModalu,
} from './ui';

/**
 * Správa → Integrace. Norma §1: seznam napojení (Meta, Google, AI, SMS,
 * E-mail) se stavem · detail ve sloupci · třetí sloupec `panelVzdy` se
 * souhrnem a „K dořešení" (končící token, neúplné napojení, SMS v testu).
 *
 * Tajné hodnoty jsou jen pro zápis a mění je jen superadmin — API vrací
 * „nastaveno", nikdy hodnotu (dřív je Nastavení → Sociální sítě posílalo
 * do prohlížeče celé). Dřív: Nastavení → Sociální sítě + Nastavení → SMS.
 */
const IKONY = { objedname: ShoppingBag, meta: Facebook, google: MapPin, ai: Sparkles, sms: MessageSquare, email: Mail };
const STAV = {
    ok: { text: 'Napojeno', barva: ZELENA },
    neuplne: { text: 'Neúplné', barva: CERVENA },
    nenapojeno: { text: 'Nenapojeno', barva: null },
    test: { text: 'Testovací režim', barva: null },
};

export function kDoreseniIntegraci(data) {
    if (!data) return [];
    const out = [];
    for (const i of data.integrace) {
        if (i.stav === 'neuplne') out.push({ id: `neuplne-${i.id}`, integrace: i.id, problem: true, titulek: `${i.nazev} · neúplné`, popis: `Chybí: ${i.chybi.join(', ')}` });
    }
    const dni = data.meta_token_dni;
    if (dni != null && dni <= data.prah_tokenu_dni) {
        out.push({ id: 'meta-token', integrace: 'meta', problem: dni < 7, titulek: 'Meta · token', popis: dni < 0 ? `vypršel před ${-dni} dny` : `končí za ${dni} dní` });
    }
    if (data.sms?.rezim === 'test') out.push({ id: 'sms-test', integrace: 'sms', problem: false, titulek: 'SMS v testovacím režimu', popis: 'Zprávy se ukládají, ale neodcházejí.' });
    if (data.sms?.rezim === 'ostry' && data.sms?.telefon && !data.sms.telefon.online) out.push({ id: 'sms-offline', integrace: 'sms', problem: true, titulek: 'SMS · telefon bistra offline', popis: 'Ostré SMS nemá kdo odeslat.' });
    if (data.objedname && !data.objedname.stahovani_zapnuto) out.push({ id: 'objedname-stahovani', integrace: 'objedname', problem: !data.objedname.zrcadlo, titulek: data.objedname.zrcadlo ? 'Objedname · zrcadlo vypnuté' : 'Objedname · stahování vypnuté', popis: 'Objednávky z Objedname nechodí.' });
    if (data.objedname?.testovaci?.length) out.push({ id: 'objedname-testovaci', integrace: 'objedname', problem: true, titulek: 'Objedname · testovací objednávky jdou ostře', popis: `${data.objedname.testovaci.join(', ')} — po zkoušce seznam vyprázdni.` });
    if (data.objedname?.prikazy === 'simulace') out.push({ id: 'objedname-simulace', integrace: 'objedname', problem: false, titulek: 'Objedname · příkazy v simulaci', popis: 'Zapisují se, ale do Objedname neodcházejí.' });
    if (!data.ai_server?.klic) out.push({ id: 'ai-server', integrace: 'ai', problem: true, titulek: 'AI · chybí klíč na serveru', popis: 'Lami, sken dokladů a banka nepoběží.' });
    return out.sort((a, b) => Number(b.problem) - Number(a.problem));
}

/** Seznam = editovatelné integrace + SMS a e-mail (stav z jiných zdrojů). */
function polozky(data) {
    if (!data) return [];
    const list = data.integrace.map((i) => ({ id: i.id, nazev: i.nazev, popis: i.popis, stav: i.stav }));
    list.push({ id: 'sms', nazev: 'SMS', popis: 'Telefon bistra jako brána', stav: data.sms.rezim === 'ostry' ? (data.sms.telefon?.online ? 'ok' : 'neuplne') : 'test' });
    list.push({ id: 'email', nazev: 'E-mail', popis: 'Odchozí pošta OS', stav: data.email.mailer && data.email.mailer !== 'log' ? 'ok' : 'nenapojeno' });
    return list;
}

export default function SpravaIntegrace({ user, isDark, isMobile, cardStyle, inputStyle, rezervaDole, onNavigate = null }) {
    const upravuje = muze(user, 'sprava.integrace', 'upravuje');
    const [data, setData] = useState(null);
    const [chyba, setChyba] = useState(null);
    const [vybranyId, setVybranyId] = useState(null);
    const [uprava, setUprava] = useState(null);
    const [hledani, setHledani] = useState('');
    const [smsPohled, setSmsPohled] = useState('brana');

    const obnov = () => nacti('/api/sprava/integrace').then(setData).catch((e) => setChyba(e.message));
    useEffect(() => { obnov(); }, []);

    const kDoreseni = useMemo(() => kDoreseniIntegraci(data), [data]);
    const seznamPolozek = useMemo(() => polozky(data), [data]);
    const q = hledani.trim().toLowerCase();
    const viditelne = seznamPolozek.filter((p) => !q || `${p.nazev} ${p.popis}`.toLowerCase().includes(q));
    const vybrana = seznamPolozek.find((p) => p.id === vybranyId) || null;
    const ml = tlumene(isDark);

    const stavStitek = (stav) => {
        const s = STAV[stav] || STAV.nenapojeno;
        return <span style={{ fontSize: 12, fontWeight: 600, color: s.barva || ml, whiteSpace: 'nowrap' }}>{s.text}</span>;
    };

    const seznam = ({ siroky }) => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <ListToolbar siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={ml} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{ value: hledani, onChange: setHledani, placeholder: 'Hledat napojení…', placeholderKratky: 'Hledat' }}
                pod={isMobile && kDoreseni.length > 0 ? <KDoreseniRadek polozky={kDoreseni} isDark={isDark} barva={SECTION_COLOR} styl={{ marginTop: 8 }}
                    renderAkce={(k) => <button type="button" onClick={() => setVybranyId(k.integrace)} style={{ fontSize: 12, fontWeight: 600, color: SECTION_COLOR, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>Otevřít</button>} /> : null} />
            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                {chyba && <p className="text-sm" style={{ color: CERVENA, padding: 16 }}>{chyba}</p>}
                {!data && !chyba && <p className="text-sm" style={{ color: ml, padding: 16 }}>Načítám…</p>}
                {viditelne.map((p) => {
                    const Ikona = IKONY[p.id] || Plug;
                    const sel = p.id === vybranyId;
                    return (
                        <button key={p.id} type="button" onClick={() => setVybranyId(p.id)} className="w-full text-left flex items-center"
                            style={{ gap: 12, padding: isMobile ? '12px 16px' : '10px 14px', minHeight: 56, cursor: 'pointer', border: 'none',
                                borderBottom: `1px solid ${cara(isDark)}`, background: sel ? (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)') : 'transparent',
                                boxShadow: sel ? `inset 3px 0 0 ${SECTION_COLOR}` : 'none' }}>
                            <Ikona size={16} style={{ color: SECTION_COLOR, flexShrink: 0 }} />
                            <div className="flex-1 min-w-0">
                                <p className="truncate font-semibold" style={{ margin: 0, color: 'var(--text-primary)', fontSize: isMobile ? 15 : 13.5 }}>{p.nazev}</p>
                                <p className="truncate" style={{ margin: '2px 0 0', fontSize: 11.5, color: ml }}>{p.popis}</p>
                            </div>
                            {stavStitek(p.stav)}
                        </button>
                    );
                })}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    );

    const detail = () => {
        if (!vybrana || !data) return null;
        const integrace = data.integrace.find((i) => i.id === vybrana.id);
        return (
            <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
                <div style={{ padding: isMobile ? '6px 14px 10px' : '16px 18px 12px', borderBottom: `1px solid ${cara(isDark)}`, flexShrink: 0 }}>
                    {isMobile && <ZpetTlacitko onClick={() => setVybranyId(null)} popis="Zpět na integrace" sklo isDark={isDark} style={{ marginBottom: 8 }} />}
                    <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                            <h2 className="font-bold" style={{ fontSize: 18, margin: 0, color: 'var(--text-primary)' }}>{vybrana.nazev}</h2>
                            <p className="text-xs" style={{ color: ml, margin: '4px 0 0' }}>{vybrana.popis} · {stavStitek(vybrana.stav)}</p>
                        </div>
                        {integrace && upravuje && !isMobile && <Tlacitko isDark={isDark} Icon={Pencil} onClick={() => setUprava(integrace)}>Upravit</Tlacitko>}
                    </div>
                </div>
                <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0, padding: isMobile ? '8px 16px' : '10px 18px' }}>
                    {integrace && (
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', columnGap: 24 }}>
                            {integrace.pole.map((p) => (
                                <Pole key={p.klic} popisek={p.nazev} isDark={isDark}>
                                    {p.tajne
                                        ? (p.nastaveno ? <span className="inline-flex items-center gap-1.5"><Lock size={12} style={{ color: ml }} /> nastaveno{p.zdroj === 'server' && <span style={{ color: ml }}> · ze serveru</span>}</span> : null)
                                        : (p.hodnota || (p.zdroj === 'server' ? <span style={{ color: ml }}>ze serveru</span> : null))}
                                </Pole>
                            ))}
                        </div>
                    )}
                    {vybrana.id === 'objedname' && data.objedname && (
                        <ObjednameNastaveni d={data.objedname} upravuje={upravuje} smiTajne={data.smi_tajne} isDark={isDark} isMobile={isMobile}
                            inputStyle={inputStyle} onData={setData} />
                    )}
                    {vybrana.id === 'meta' && data.meta_token_dni != null && (
                        <p className="text-sm" style={{ color: data.meta_token_dni < 7 ? CERVENA : ml, margin: '8px 0 0' }}>
                            {data.meta_token_dni < 0 ? `Token vypršel před ${-data.meta_token_dni} dny.` : `Token platí ještě ${data.meta_token_dni} dní.`}
                        </p>
                    )}
                    {vybrana.id === 'ai' && (
                        <>
                            <Nadpis isDark={isDark} mt={18}>Na serveru</Nadpis>
                            <Pole popisek="Hlavní klíč (.env)" isDark={isDark}>{data.ai_server.klic ? 'nastaven' : <span style={{ color: CERVENA }}>chybí</span>}</Pole>
                            {Object.entries(data.ai_server.modely).map(([k, v]) => <Pole key={k} popisek={`Model · ${k}`} isDark={isDark}>{v}</Pole>)}
                            <Pole popisek="Denní limit volání Lami" isDark={isDark}>{data.ai_server.lami_denni_limit}</Pole>
                        </>
                    )}
                    {vybrana.id === 'email' && (
                        <>
                            <Pole popisek="Způsob odesílání" isDark={isDark}>{data.email.mailer === 'graph' ? 'Microsoft Graph (Office 365)' : data.email.mailer}</Pole>
                            <Pole popisek="Odesílatel" isDark={isDark}>{[data.email.jmeno, data.email.od && `<${data.email.od}>`].filter(Boolean).join(' ')}</Pole>
                            <p className="text-xs" style={{ color: ml, marginTop: 8 }}>Nastavuje se na serveru (.env).</p>
                        </>
                    )}
                    {vybrana.id === 'sms' && (
                        <div style={{ marginTop: 4 }}>
                            <p className="text-xs" style={{ color: ml, margin: '0 0 10px' }}>
                                Tady je brána: režim, zkušební SMS a co OS odeslal. Konverzace se zákazníky (odchozí i příchozí SMS u čísla) jsou v Zákazníci → Komunikace.
                                {onNavigate && muze(user, 'zakaznici.komunikace') && (
                                    <button type="button" onClick={() => onNavigate('zakaznici', { tab: 'komunikace' })} className="font-semibold"
                                        style={{ marginLeft: 6, background: 'none', border: 'none', padding: 0, color: SECTION_COLOR, cursor: 'pointer' }}>Otevřít</button>
                                )}
                            </p>
                            <div className="flex flex-wrap gap-1.5" style={{ marginBottom: 12 }}>
                                <PohledPill label="Brána a odeslané" aktivni={smsPohled === 'brana'} onClick={() => setSmsPohled('brana')} isDark={isDark} mutedColor={ml} />
                                <PohledPill label="Šablony" aktivni={smsPohled === 'sablony'} onClick={() => setSmsPohled('sablony')} isDark={isDark} mutedColor={ml} />
                            </div>
                            {smsPohled === 'brana' ? <SmsPrehled isDark={isDark} /> : <SmsSablony isDark={isDark} />}
                        </div>
                    )}
                    {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
                </div>
            </div>
        );
    };

    const panel = () => (
        <div className="rounded-xl" style={{ ...cardStyle, padding: 16 }}>
            <Nadpis isDark={isDark}>Napojení</Nadpis>
            <RadekPanelu isDark={isDark} popis="Napojeno" hodnota={seznamPolozek.filter((p) => p.stav === 'ok').length} />
            <RadekPanelu isDark={isDark} popis="Neúplné" hodnota={seznamPolozek.filter((p) => p.stav === 'neuplne').length} barva={seznamPolozek.some((p) => p.stav === 'neuplne') ? CERVENA : null} />
            <RadekPanelu isDark={isDark} popis="Nenapojeno" hodnota={seznamPolozek.filter((p) => p.stav === 'nenapojeno').length} />
            {data && !data.smi_tajne && (
                <p className="text-xs flex items-start gap-1.5" style={{ color: ml, margin: '10px 0 0' }}><Lock size={12} style={{ marginTop: 2 }} /> Tokeny a klíče mění jen superadmin.</p>
            )}
            <Nadpis isDark={isDark} mt={16}>K dořešení</Nadpis>
            <KDoreseniSeznam polozky={kDoreseni} isDark={isDark} prazdne={data ? 'Všechna napojení jsou v pořádku.' : 'Načítám…'}
                renderAkce={(k) => <button type="button" onClick={() => setVybranyId(k.integrace)} style={{ fontSize: 12, fontWeight: 600, color: SECTION_COLOR, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>Otevřít</button>} />
        </div>
    );

    return (
        <div className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ModulLayout isMobile={isMobile} vybrano={!!vybranyId} mobilRezim mobilPanel={false} panelVzdy barva={SECTION_COLOR} isDark={isDark}
                seznam={seznam} detail={detail} panel={panel} />
            {uprava && (
                <UpravaIntegrace integrace={uprava} smiTajne={data?.smi_tajne} isDark={isDark} isMobile={isMobile} inputStyle={inputStyle}
                    onClose={() => setUprava(null)} onUlozeno={(d) => { setData(d); setUprava(null); }} />
            )}
        </div>
    );
}

function UpravaIntegrace({ integrace, smiTajne, isDark, isMobile, inputStyle, onClose, onUlozeno }) {
    const [hodnoty, setHodnoty] = useState(() => Object.fromEntries(integrace.pole.map((p) => [p.klic, p.tajne ? '' : (p.hodnota ?? '')])));
    const [smazat, setSmazat] = useState([]);
    const [ukladam, setUkladam] = useState(false);
    const [chyba, setChyba] = useState(null);
    const uloz = async () => {
        setUkladam(true); setChyba(null);
        const posilane = Object.fromEntries(integrace.pole
            .filter((p) => (p.tajne ? smiTajne && hodnoty[p.klic].trim() : true))
            .map((p) => [p.klic, hodnoty[p.klic]]));
        try { onUlozeno(await posli(`/api/sprava/integrace/${integrace.id}`, 'PUT', { hodnoty: posilane, smazat })); }
        catch (e) { setChyba(e.message); setUkladam(false); }
    };
    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis={integrace.nazev} ikona={Plug} barva={SECTION_COLOR} sirka={520}
            paticka={<PatickaModalu onZrusit={onClose} onPotvrdit={uloz} ukladam={ukladam} isDark={isDark} isMobile={isMobile} />}>
            <div style={{ padding: 16 }}>
                {integrace.pole.map((p) => {
                    const zamceno = p.tajne && !smiTajne;
                    const maze = smazat.includes(p.klic);
                    return (
                        <label key={p.klic} style={{ display: 'block', marginBottom: 12 }}>
                            <span className="flex items-center gap-1.5" style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
                                {p.tajne && <Lock size={11} />} {p.nazev}
                                {p.tajne && p.nastaveno && p.zdroj === 'os' && smiTajne && (
                                    <button type="button" onClick={() => setSmazat((s) => (maze ? s.filter((k) => k !== p.klic) : [...s, p.klic]))}
                                        style={{ marginLeft: 'auto', fontSize: 11, color: maze ? CERVENA : 'var(--text-secondary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                                        {maze ? 'bude smazáno · vrátit' : 'smazat'}
                                    </button>
                                )}
                            </span>
                            <input type={p.typ === 'date' ? 'date' : p.tajne ? 'password' : 'text'} autoComplete="off" disabled={zamceno || maze}
                                value={hodnoty[p.klic]} onChange={(e) => setHodnoty((h) => ({ ...h, [p.klic]: e.target.value }))}
                                placeholder={p.tajne ? (zamceno ? 'mění jen superadmin' : p.nastaveno ? 'nastaveno — vyplň jen pro změnu' : 'vlož hodnotu') : ''}
                                style={{ ...inputStyle, opacity: zamceno || maze ? 0.5 : 1 }} />
                        </label>
                    );
                })}
                <p className="text-xs flex items-start gap-1.5" style={{ color: tlumene(isDark), margin: 0 }}>
                    <AlertTriangle size={12} style={{ marginTop: 2, flexShrink: 0 }} /> Tajné hodnoty se ukládají šifrovaně a zpátky se už nezobrazí.
                </p>
                {chyba && <p className="text-sm" style={{ color: CERVENA, margin: '8px 0 0' }}>{chyba}</p>}
            </div>
        </ModalShell>
    );
}

/** Kroky postupného přebírání objednávek z Objedname. */
const REZIMY_OBJEDNAME = [
    { id: 'cteni', nazev: 'Jen čtení', popis: 'Dnešní stav: objednávky z Objedname jen stahujeme, celý proces běží v Objedname. Kuchyňský displej je nebere.' },
    { id: 'potvrzene', nazev: 'Krok 1 · přebíráme potvrzené', popis: 'Obsluha potvrzuje v pokladně Objedname. Potvrzené a odložené objednávky přebíráme, dál je vede náš systém (kuchyň, rozvoz). Z Objedname platí už jen Storno a Hotová.' },
    { id: 'nove', nazev: 'Krok 2 · přebíráme nové', popis: 'Nové a odložené objednávky přebíráme hned, potvrzení dělá naše pokladna. Pokladnu Objedname už nikdo nepotřebuje.' },
];
const PRIKAZY_OBJEDNAME = [
    { id: 'vypnuto', nazev: 'Vypnuto' },
    { id: 'simulace', nazev: 'Simulace', popis: 'jen se zapíše, co by se poslalo' },
    { id: 'ostre', nazev: 'Ostře' },
];

function ObjednameNastaveni({ d, upravuje, smiTajne, isDark, isMobile, inputStyle, onData }) {
    const ml = tlumene(isDark);
    const [rezim, setRezim] = useState(d.rezim);
    const [prikazy, setPrikazy] = useState(d.prikazy);
    const [ukladam, setUkladam] = useState(false);
    const [chyba, setChyba] = useState(null);
    const [pinPro, setPinPro] = useState(null);
    const [obnovuji, setObnovuji] = useState(false);
    useEffect(() => { setRezim(d.rezim); setPrikazy(d.prikazy); }, [d.rezim, d.prikazy]);
    const zmeneno = rezim !== d.rezim || prikazy !== d.prikazy;

    // Načte rozvozce znovu a smaže PINy lidí, kteří už odešli.
    const aktualizujRozvozce = async () => {
        const odesli = d.rozvozci.filter((r) => r.odesel && r.pin);
        if (odesli.length && !window.confirm(`Smazat PIN lidem, kteří už nejsou aktivní (${odesli.map((r) => r.jmeno).join(', ')})?`)) return;
        setObnovuji(true); setChyba(null);
        try { onData(await posli('/api/sprava/integrace/objedname/rozvozci/aktualizovat', 'POST', {})); }
        catch (e) { setChyba(e.message); }
        finally { setObnovuji(false); }
    };

    const uloz = async () => {
        if (rezim !== d.rezim && !window.confirm(rezim === 'cteni'
            ? 'Vrátit na jen čtení? Objednávky z Objedname zmizí z kuchyňského displeje a proces povede zase Objedname.'
            : `Přepnout na „${REZIMY_OBJEDNAME.find((r) => r.id === rezim).nazev}"? Platí pro objednávky od teď.`)) return;
        setUkladam(true); setChyba(null);
        try { onData(await posli('/api/sprava/integrace/objedname/rezim', 'PUT', { rezim, prikazy })); }
        catch (e) { setChyba(e.message); }
        setUkladam(false);
    };

    const karta = (aktivni) => ({
        textAlign: 'left', width: '100%', padding: '10px 12px', borderRadius: 10, cursor: upravuje ? 'pointer' : 'default',
        border: `${aktivni ? 2 : 1}px solid ${aktivni ? SECTION_COLOR : cara(isDark)}`,
        background: aktivni ? (isDark ? 'rgba(59,130,246,0.12)' : 'rgba(59,130,246,0.06)') : 'transparent',
    });

    const [prepinam, setPrepinam] = useState(false);
    const stahovani = async (zapnuto) => {
        if (!zapnuto && !window.confirm(d.zrcadlo
            ? 'Vypnout zrcadlo? Tento server se přestane ptát Objedname a objednávky sem přestanou chodit.'
            : 'Vypnout stahování z Objedname? Objednávky z Objedname přestanou chodit do OS — kuchyň ani rozvoz je neuvidí.')) return;
        setPrepinam(true); setChyba(null);
        try { onData(await posli('/api/sprava/integrace/objedname/stahovani', 'PUT', { zapnuto })); }
        catch (e) { setChyba(e.message); }
        setPrepinam(false);
    };
    const posledni = d.posledni_stazeni ? new Date(d.posledni_stazeni) : null;
    const stari = posledni ? Math.round((Date.now() - posledni.getTime()) / 1000) : null;

    return (
        <>
            <Nadpis isDark={isDark} mt={18}>{d.zrcadlo ? 'Zrcadlo produkce' : 'Stahování z Objedname'}</Nadpis>
            {d.zrcadlo && (
                <p className="text-xs" style={{ color: ml, margin: '0 0 8px' }}>
                    Tenhle server (stage) stahuje objednávky z Objedname sám, stejně jako produkce — živá data na zkoušení. Do Objedname nic neposílá, příkazy jdou nejvýš v simulaci.
                </p>
            )}
            <div className="flex items-center gap-3" style={{ padding: '8px 0' }}>
                <span className="flex-1" style={{ fontSize: 13.5, color: 'var(--text-primary)' }}>
                    {d.stahovani_zapnuto ? <span style={{ color: ZELENA, fontWeight: 600 }}>Zapnuto</span> : <span style={{ color: CERVENA, fontWeight: 600 }}>Vypnuto</span>}
                    <span style={{ color: ml, fontSize: 12 }}>
                        {posledni ? ` · naposledy staženo ${stari < 90 ? `před ${stari} s` : posledni.toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ' · zatím nic nestaženo'}
                    </span>
                </span>
                {upravuje && (
                    <Tlacitko isDark={isDark} plna={!d.stahovani_zapnuto} disabled={prepinam} onClick={() => stahovani(!d.stahovani_zapnuto)}>
                        {d.stahovani_zapnuto ? 'Vypnout' : 'Zapnout'}
                    </Tlacitko>
                )}
            </div>

            <Nadpis isDark={isDark} mt={18}>Přebírání objednávek</Nadpis>
            <div className="flex flex-col gap-2">
                {REZIMY_OBJEDNAME.map((r) => (
                    <button key={r.id} type="button" disabled={!upravuje} onClick={() => setRezim(r.id)} style={karta(rezim === r.id)}>
                        <span className="flex items-center gap-2 font-semibold" style={{ fontSize: 13.5, color: 'var(--text-primary)' }}>
                            {r.nazev}
                            {d.rezim === r.id && <span style={{ fontSize: 11, fontWeight: 600, color: ZELENA }}>· teď{r.id !== 'cteni' && d.od ? ` od ${d.od.slice(8, 10)}. ${d.od.slice(5, 7)}. ${d.od.slice(11)}` : ''}</span>}
                        </span>
                        <span className="block" style={{ fontSize: 12, color: ml, marginTop: 3 }}>{r.popis}</span>
                    </button>
                ))}
            </div>

            <Nadpis isDark={isDark} mt={18}>Příkazy do Objedname</Nadpis>
            <p className="text-xs" style={{ color: ml, margin: '0 0 8px' }}>
                Na cestě (při převzetí dávky řidičem), Hotová (po předání zákazníkovi), v kroku 2 i potvrzení a storno.
            </p>
            <div className="flex flex-wrap gap-1.5">
                {PRIKAZY_OBJEDNAME.map((p) => {
                    const zamceno = p.id === 'ostre' && !d.ostre_povoleno;
                    return (
                        <PohledPill key={p.id} label={zamceno ? `${p.nazev} (jen produkce)` : p.nazev} aktivni={prikazy === p.id}
                            onClick={() => upravuje && !zamceno && setPrikazy(p.id)} isDark={isDark} mutedColor={ml} />
                    );
                })}
            </div>

            <TestovaciObjednavky d={d} upravuje={upravuje} isDark={isDark} inputStyle={inputStyle} onData={onData} />

            {upravuje && zmeneno && (
                <div className="flex items-center gap-2" style={{ marginTop: 12 }}>
                    <Tlacitko isDark={isDark} plna onClick={uloz} disabled={ukladam}>{ukladam ? 'Ukládám…' : 'Uložit'}</Tlacitko>
                    <Tlacitko isDark={isDark} onClick={() => { setRezim(d.rezim); setPrikazy(d.prikazy); }}>Zrušit</Tlacitko>
                </div>
            )}
            {chyba && <p className="text-sm" style={{ color: CERVENA, margin: '8px 0 0' }}>{chyba}</p>}

            <Nadpis isDark={isDark} mt={18} vpravo={upravuje && smiTajne && (
                <Tlacitko isDark={isDark} Icon={RefreshCw} onClick={aktualizujRozvozce} disabled={obnovuji}>{obnovuji ? 'Načítám…' : 'Aktualizovat seznam'}</Tlacitko>
            )}>Rozvozci · PIN do Objedname</Nadpis>
            <p className="text-xs" style={{ color: ml, margin: '0 0 6px' }}>
                Osobní kód rozvozce v Objedname. Posílá se při „Na cestě", ať Objedname zná peněženku rozvozce a vyúčtování sedí.
            </p>
            {d.rozvozci.length === 0 && <p className="text-sm" style={{ color: ml }}>Nikdo nemá kariéru Rozvoz ani za posledních 60 dní nejel rozvoz.</p>}
            {d.rozvozci.map((r) => (
                <div key={r.id} className="flex items-center gap-2" style={{ padding: '8px 0', borderBottom: `1px solid ${cara(isDark)}` }}>
                    <span className="flex-1 min-w-0 truncate" style={{ fontSize: 13.5, color: r.odesel ? ml : 'var(--text-primary)' }}>{r.jmeno}{r.odesel && <span style={{ fontSize: 11.5, color: CERVENA, marginLeft: 6 }}>odešel</span>}</span>
                    {r.pin
                        ? <span className="inline-flex items-center gap-1" style={{ fontSize: 12, color: ZELENA }}><Lock size={11} /> PIN nastaven</span>
                        : <span style={{ fontSize: 12, color: ml }}>bez PINu</span>}
                    {upravuje && smiTajne && !isMobile && (
                        <Tlacitko isDark={isDark} Icon={KeyRound} onClick={() => setPinPro(r)}>{r.pin ? 'Změnit' : 'Nastavit'}</Tlacitko>
                    )}
                </div>
            ))}
            {upravuje && !smiTajne && <p className="text-xs flex items-start gap-1.5" style={{ color: ml, margin: '8px 0 0' }}><Lock size={12} style={{ marginTop: 2 }} /> PINy mění jen superadmin.</p>}
            {!d.stahovani && <p className="text-xs" style={{ color: CERVENA, margin: '12px 0 0' }}>Stahování z Objedname nemá na serveru přihlášení (.env) — objednávky nechodí.</p>}

            <LogPrikazu isDark={isDark} />

            {pinPro && <PinRozvozce rozvozce={pinPro} isDark={isDark} isMobile={isMobile} inputStyle={inputStyle}
                onClose={() => setPinPro(null)} onUlozeno={(nova) => { onData(nova); setPinPro(null); }} />}
        </>
    );
}

function PinRozvozce({ rozvozce, isDark, isMobile, inputStyle, onClose, onUlozeno }) {
    const [pin, setPin] = useState('');
    const [ukladam, setUkladam] = useState(false);
    const [chyba, setChyba] = useState(null);
    const uloz = async (hodnota) => {
        setUkladam(true); setChyba(null);
        try { onUlozeno(await posli(`/api/sprava/integrace/objedname/rozvozci/${rozvozce.id}`, 'PUT', { pin: hodnota })); }
        catch (e) { setChyba(e.message); setUkladam(false); }
    };
    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis={`PIN do Objedname · ${rozvozce.jmeno}`} ikona={KeyRound} barva={SECTION_COLOR} sirka={420}
            paticka={<PatickaModalu onZrusit={onClose} onPotvrdit={() => pin.trim() && uloz(pin.trim())} ukladam={ukladam} isDark={isDark} isMobile={isMobile} />}>
            <div style={{ padding: 16 }}>
                <input type="password" inputMode="numeric" autoComplete="off" autoFocus value={pin} onChange={(e) => setPin(e.target.value)}
                    placeholder={rozvozce.pin ? 'nastaveno — vyplň jen pro změnu' : 'osobní kód z Objedname'} style={inputStyle} />
                <p className="text-xs flex items-start gap-1.5" style={{ color: tlumene(isDark), margin: '8px 0 0' }}>
                    <AlertTriangle size={12} style={{ marginTop: 2, flexShrink: 0 }} /> Ukládá se šifrovaně a zpátky se už nezobrazí.
                </p>
                {rozvozce.pin && (
                    <button type="button" onClick={() => uloz('')} disabled={ukladam}
                        style={{ marginTop: 12, fontSize: 12, color: CERVENA, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>Smazat PIN</button>
                )}
                {chyba && <p className="text-sm" style={{ color: CERVENA, margin: '8px 0 0' }}>{chyba}</p>}
            </div>
        </ModalShell>
    );
}

const STAV_PRIKAZU = {
    ceka: { text: 'čeká', barva: null },
    simulovano: { text: 'simulace', barva: SECTION_COLOR },
    odeslano: { text: 'odesláno', barva: ZELENA },
    chyba: { text: 'chyba', barva: CERVENA },
};
const AKCE_PRIKAZU = { DELIVERED: 'Hotová', CREATE_BATCH: 'Na cestě', CONFIRM: 'Potvrdit', CANCEL: 'Storno' };

/** Log příkazů do Objedname — co se poslalo, nebo v simulaci poslalo by. */
function LogPrikazu({ isDark }) {
    const ml = tlumene(isDark);
    const [radky, setRadky] = useState(null);
    const [otevreny, setOtevreny] = useState(null);
    const obnov = () => nacti('/api/sprava/integrace/objedname/prikazy').then(setRadky).catch(() => setRadky([]));
    useEffect(() => { obnov(); const t = setInterval(obnov, 15000); return () => clearInterval(t); }, []);

    return (
        <>
            <Nadpis isDark={isDark} mt={18}>Log příkazů</Nadpis>
            {radky === null && <p className="text-sm" style={{ color: ml }}>Načítám…</p>}
            {radky?.length === 0 && <p className="text-sm" style={{ color: ml }}>Zatím žádný příkaz.</p>}
            {radky?.map((r) => {
                const st = STAV_PRIKAZU[r.stav] || STAV_PRIKAZU.ceka;
                const cas = r.cas ? new Date(r.cas).toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';
                return (
                    <div key={r.id} style={{ borderBottom: `1px solid ${cara(isDark)}` }}>
                        <button type="button" onClick={() => setOtevreny(otevreny === r.id ? null : r.id)} className="w-full flex items-center gap-2 text-left"
                            style={{ padding: '7px 0', background: 'none', border: 'none', cursor: 'pointer' }}>
                            <span className="tabular-nums" style={{ fontSize: 12, color: ml, width: 118, flexShrink: 0 }}>{cas}</span>
                            <span className="font-semibold" style={{ fontSize: 13, color: 'var(--text-primary)', width: 72, flexShrink: 0 }}>{AKCE_PRIKAZU[r.akce] || r.akce}</span>
                            <span className="flex-1 min-w-0 truncate" style={{ fontSize: 13, color: 'var(--text-primary)' }}>{r.objednavka}</span>
                            <span style={{ fontSize: 12, fontWeight: 600, color: st.barva || ml }}>{st.text}</span>
                        </button>
                        {otevreny === r.id && (
                            <div style={{ fontSize: 12, color: ml, padding: '0 0 8px', wordBreak: 'break-all' }}>
                                {r.pozadavek && <div><b>Požadavek:</b> {r.pozadavek}</div>}
                                {r.odpoved && <div><b>Odpověď:</b> {r.odpoved}</div>}
                            </div>
                        )}
                    </div>
                );
            })}
        </>
    );
}

/** Testovací objednávky — pro ně jdou příkazy ostře vždy (i na stagi, i neověřená akce). */
function TestovaciObjednavky({ d, upravuje, isDark, inputStyle, onData }) {
    const ml = tlumene(isDark);
    const puvodni = (d.testovaci || []).join(', ');
    const [text, setText] = useState(puvodni);
    const [chyba, setChyba] = useState(null);
    useEffect(() => { setText(puvodni); }, [puvodni]);
    const uloz = async (hodnota) => {
        setChyba(null);
        try { onData(await posli('/api/sprava/integrace/objedname/testovaci', 'PUT', { obj_ids: hodnota })); }
        catch (e) { setChyba(e.message); }
    };
    return (
        <div style={{ marginTop: 12 }}>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Testovací objednávky (čísla z Objedname)</span>
            <div className="flex items-center gap-2" style={{ marginTop: 4 }}>
                <input value={text} onChange={(e) => setText(e.target.value)} disabled={!upravuje} placeholder="prázdné = pojistka pro všechno" style={{ ...inputStyle, flex: 1 }} />
                {upravuje && text.trim() !== puvodni && <Tlacitko isDark={isDark} plna onClick={() => uloz(text)}>Uložit</Tlacitko>}
                {upravuje && puvodni && text.trim() === puvodni && <Tlacitko isDark={isDark} onClick={() => uloz('')}>Vyprázdnit</Tlacitko>}
            </div>
            <p className="text-xs" style={{ color: puvodni ? CERVENA : ml, margin: '4px 0 0' }}>
                {puvodni ? 'Pro tyto objednávky jdou příkazy do Objedname OSTŘE — i na stagi. Po zkoušce seznam vyprázdni.' : 'Pro zkoušku: příkazy pro zapsané objednávky půjdou ostře i na stagi. Ostatní podle režimu výše.'}
            </p>
            {chyba && <p className="text-sm" style={{ color: CERVENA, margin: '4px 0 0' }}>{chyba}</p>}
        </div>
    );
}
